import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from 'shared/ui/Button'
import { Dropdown, DropdownContent } from 'shared/ui/Dropdown'
import { Error } from 'shared/ui/Error'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { Label } from 'shared/ui/Label'
import { type ICrm } from 'entities/Integrations/lib/crm'
import { DropdownCrmContent } from './DropdownCrmContent'

import styles from './styles.module.scss'

type ICrmSelectFieldProps = {
  value: ICrm | null
  onChange: (crm: ICrm | null) => void
  label?: string
  error?: string
  placeholder?: string
}

export const CrmSelectField = ({
  value,
  onChange,
  label,
  error,
  placeholder = 'Select',
}: ICrmSelectFieldProps) => {
  const selectRef = useRef<HTMLLabelElement>(null)
  const [selectWidth, setSelectWidth] = useState<number>()
  const [selectHeight, setSelectHeight] = useState<number>()

  const updateDimensions = useCallback(() => {
    if (selectRef.current) {
      setSelectWidth(selectRef.current.clientWidth)
      setSelectHeight(selectRef.current.clientHeight)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('resize', updateDimensions)
    updateDimensions()

    return () => {
      window.removeEventListener('resize', updateDimensions)
    }
  }, [updateDimensions])

  return (
    <div className={styles.wrap}>
      {label && <Label marginTop={0} label={{ text: label, className: styles.label }} />}
      <Dropdown
        placement={'bottom-start'}
        width={selectWidth}
        margin={selectHeight ? -selectHeight : undefined}
        triggerComponent={() => (
          <TextFieldPlaceholder
            ref={selectRef}
            rightActions={[makeTextFieldIcon({ icon: 'chevronDown' })]}
            placeholder={
              value ? (
                <Button
                  className={styles.placeholderItem}
                  typeBtn='menuItem'
                  type='button'
                  icon={value.icon}
                  text={value.name}
                  fullWidth
                />
              ) : (
                placeholder
              )
            }
            variant={'stroke'}
            size={'small'}
            className={classNames(styles.textField, {
              [styles.placeholder]: !value,
              [styles.errored]: !!error,
            })}
          />
        )}
        customComponent={(onClose) => (
          <DropdownContent noPadding>
            <DropdownCrmContent value={value} onChange={onChange} onClose={onClose} />
          </DropdownContent>
        )}
      />
      {error && <Error type={'text'} error={error} size={'small'} />}
    </div>
  )
}
